export interface FillOptions {
  color: string;
}

export class Fill {
  public color: string;
  constructor(options: FillOptions) {
    this.color = options.color;
  }
}

export interface StrokeOptions {
  color: string;
  width: number;
  lineDash?: number[];
  lineDashOffset?: number;
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'bevel' | 'round' | 'miter';
  miterLimit?: number;
}

export class Stroke {
  public color: string;
  public width: number;
  public lineDash?: number[];
  public lineDashOffset?: number;
  public lineCap: 'butt' | 'round' | 'square';
  public lineJoin: 'bevel' | 'round' | 'miter';
  public miterLimit: number;

  constructor(options: StrokeOptions) {
    this.color = options.color;
    this.width = options.width;
    this.lineDash = options.lineDash;
    this.lineDashOffset = options.lineDashOffset;
    this.lineCap = options.lineCap || 'round';
    this.lineJoin = options.lineJoin || 'round';
    this.miterLimit = options.miterLimit || 10;
  }
}

export interface CircleStyleOptions {
  radius: number;
  fill?: Fill;
  stroke?: Stroke;
  displacement?: [number, number];
  rotation?: number;
  scale?: number;
}

export class CircleStyle {
  public radius: number;
  public fill?: Fill;
  public stroke?: Stroke;
  public displacement?: [number, number];
  public rotation: number;
  public scale: number;

  constructor(options: CircleStyleOptions) {
    this.radius = options.radius;
    this.fill = options.fill;
    this.stroke = options.stroke;
    this.displacement = options.displacement;
    this.rotation = options.rotation || 0;
    this.scale = options.scale || 1;
  }
}

export interface IconStyleOptions {
  src: string;
  anchor?: [number, number];
  scale?: number;
  rotation?: number;
  opacity?: number;
  size?: [number, number];
  color?: string;
  crossOrigin?: string;
}

export class IconStyle {
  public src: string;
  public anchor: [number, number];
  public scale: number;
  public rotation: number;
  public opacity: number;
  public size?: [number, number];
  public color?: string;

  constructor(options: IconStyleOptions) {
    this.src = options.src;
    this.anchor = options.anchor || [0.5, 0.5];
    this.scale = options.scale || 1;
    this.rotation = options.rotation || 0;
    this.opacity = options.opacity || 1;
    this.size = options.size;
    this.color = options.color;
  }
}

export interface TextStyleOptions {
  text: string | ((feature: any) => string);
  font?: string;
  fill?: Fill;
  stroke?: Stroke;
  offsetX?: number;
  offsetY?: number;
  textAlign?: 'left' | 'center' | 'right';
  textBaseline?: 'top' | 'middle' | 'bottom';
  rotation?: number;
  rotateWithView?: boolean;
  overflow?: boolean;
  placement?: 'point' | 'line';
  maxAngle?: number;
  padding?: [number, number, number, number];
  backgroundFill?: Fill;
  backgroundStroke?: Stroke;
}

export class TextStyle {
  public text: string | ((feature: any) => string);
  public font: string;
  public fill?: Fill;
  public stroke?: Stroke;
  public offsetX: number;
  public offsetY: number;
  public textAlign: 'left' | 'center' | 'right';
  public textBaseline: 'top' | 'middle' | 'bottom';
  public rotation: number;

  constructor(options: TextStyleOptions) {
    this.text = options.text;
    this.font = options.font || '14px Inter, sans-serif';
    this.fill = options.fill;
    this.stroke = options.stroke;
    this.offsetX = options.offsetX || 0;
    this.offsetY = options.offsetY || 0;
    this.textAlign = options.textAlign || 'center';
    this.textBaseline = options.textBaseline || 'middle';
    this.rotation = options.rotation || 0;
  }
}

export interface RegularShapeStyleOptions {
  points: number;
  radius: number;
  radius2?: number;
  angle?: number;
  fill?: Fill;
  stroke?: Stroke;
  rotation?: number;
  displacement?: [number, number];
}

export class RegularShapeStyle {
  public points: number;
  public radius: number;
  public radius2?: number;
  public angle: number;
  public fill?: Fill;
  public stroke?: Stroke;
  public rotation: number;

  constructor(options: RegularShapeStyleOptions) {
    this.points = options.points;
    this.radius = options.radius;
    this.radius2 = options.radius2;
    this.angle = options.angle || 0;
    this.fill = options.fill;
    this.stroke = options.stroke;
    this.rotation = options.rotation || 0;
  }
}

export interface StyleOptions {
  fill?: Fill;
  stroke?: Stroke;
  image?: CircleStyle | IconStyle | RegularShapeStyle;
  text?: TextStyle;
  zIndex?: number;
}

export class Style {
  public fill?: Fill;
  public stroke?: Stroke;
  public image?: CircleStyle | IconStyle | RegularShapeStyle;
  public text?: TextStyle;
  public zIndex: number;

  constructor(options: StyleOptions = {}) {
    this.fill = options.fill;
    this.stroke = options.stroke;
    this.image = options.image;
    this.text = options.text;
    this.zIndex = options.zIndex || 0;
  }
}

export type StyleFunction = (feature: any, resolution: number) => Style | Style[];

export interface MilSymbolStyleOptions {
  sidc: string;
  size?: number;
  affiliation?: 'friend' | 'hostile' | 'neutral' | 'unknown';
  status?: 'present' | 'planned' | 'anticipated';
  echelon?: string;
  modifiers?: Record<string, string>;
  direction?: number;
  speed?: number;
  colorScheme?: 'light' | 'medium' | 'dark';
}

export class MilSymbolStyle {
  public sidc: string;
  public size: number;
  public affiliation: string;
  public colorScheme: string;

  constructor(options: MilSymbolStyleOptions) {
    this.sidc = options.sidc;
    this.size = options.size || 35;
    this.affiliation = options.affiliation || 'unknown';
    this.colorScheme = options.colorScheme || 'medium';
  }
}

export interface PaintLayout {
  paint: Record<string, any>;
  layout: Record<string, any>;
}

export function styleToCircleProps(style: Style | undefined, opacity: number): PaintLayout {
  const img = style?.image instanceof CircleStyle ? (style.image as CircleStyle) : undefined;
  const fill = img?.fill || style?.fill;
  const stroke = img?.stroke || style?.stroke;
  const radius = img?.radius ?? 5;
  return {
    paint: {
      'circle-radius': radius,
      'circle-color': fill?.color || '#3b82f6',
      'circle-opacity': opacity,
      'circle-stroke-color': stroke?.color || '#ffffff',
      'circle-stroke-width': stroke?.width ?? 1,
      'circle-stroke-opacity': opacity,
    },
    layout: {},
  };
}

export function styleToLineProps(style: Style | undefined, opacity: number): PaintLayout {
  const stroke = style?.stroke;
  const paint: Record<string, any> = {
    'line-color': stroke?.color || '#3b82f6',
    'line-width': stroke?.width ?? 2,
    'line-opacity': opacity,
  };
  if (stroke?.lineDash && stroke.lineDash.length > 0) {
    paint['line-dasharray'] = stroke.lineDash;
  }
  return {
    paint,
    layout: {
      'line-cap': stroke?.lineCap || 'round',
      'line-join': stroke?.lineJoin || 'round',
    },
  };
}

export function styleToFillProps(style: Style | undefined, opacity: number): PaintLayout {
  const fill = style?.fill;
  return {
    paint: {
      'fill-color': fill?.color || '#3b82f6',
      'fill-opacity': opacity * 0.5,
      'fill-outline-color': style?.stroke?.color || fill?.color || '#1d4ed8',
    },
    layout: {},
  };
}

/**
 * Convert a TextStyle.text value to a MapLibre `text-field` expression.
 *  - bare property name (e.g. "name")        -> ['get', 'name']
 *  - template with {field} placeholders      -> format expression with get's
 *  - literal wrapped as `"...literal..."`    -> stripped to literal text
 *  - a function                              -> empty string (cannot be expressed)
 */
function textValueToField(text: string | ((feature: any) => string)): any {
  if (typeof text === 'function') return '';
  if (text.length === 0) return '';
  // explicit literal: surrounded by double quotes
  if (text.length >= 2 && text.startsWith('"') && text.endsWith('"')) {
    return text.slice(1, -1);
  }
  // template with {prop} substitutions
  const tplRe = /\{([^}]+)\}/g;
  if (tplRe.test(text)) {
    const parts: any[] = ['concat'];
    let last = 0;
    text.replace(/\{([^}]+)\}/g, (m, prop, off) => {
      if (off > last) parts.push(text.slice(last, off));
      parts.push(['coalesce', ['to-string', ['get', prop]], '']);
      last = off + m.length;
      return m;
    });
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  }
  // bare identifier? treat as property key
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(text)) return ['get', text];
  // anything else: treat as literal
  return text;
}

export function styleToTextProps(style: Style | undefined, opacity: number): PaintLayout {
  const text = style?.text;
  if (!text) return { paint: {}, layout: {} };
  const fontSizeMatch = /([0-9]+(?:\.[0-9]+)?)px/.exec(text.font);
  const fontSize = fontSizeMatch ? parseFloat(fontSizeMatch[1]) : 14;
  return {
    paint: {
      'text-color': text.fill?.color || '#111827',
      'text-opacity': opacity,
      'text-halo-color': text.stroke?.color || '#ffffff',
      'text-halo-width': text.stroke?.width ?? 1,
    },
    layout: {
      'text-field': textValueToField(text.text),
      'text-size': fontSize,
      'text-offset': [text.offsetX / fontSize, text.offsetY / fontSize],
      'text-anchor':
        text.textBaseline === 'top' ? 'top'
        : text.textBaseline === 'bottom' ? 'bottom'
        : text.textAlign === 'left' ? 'left'
        : text.textAlign === 'right' ? 'right'
        : 'center',
      'text-rotate': (text.rotation * 180) / Math.PI,
      'text-allow-overlap': false,
    },
  };
}
